import { Apiv2Context } from "./apiv2Context";
import { queryParameterSerialization } from "./apiv2QueryParameterSerialization";
import { serializeQueryParameters } from "./queryParameterSerializer";

export type ErrorWrapper<TError> =
  | TError
  | { status: "unknown"; payload: { message: string; timestamp: string } };

export type Apiv2FetcherOptions<TBody, THeaders, TQueryParams, TPathParams> = {
  url: string;
  method: string;
  body?: TBody;
  headers?: THeaders;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  signal?: AbortSignal;
} & Apiv2Context["fetcherOptions"];

const isFormDataBody = (body: unknown): body is FormData => {
  return typeof FormData !== "undefined" && body instanceof FormData;
};

const isBlobBody = (body: unknown): body is Blob => {
  return typeof Blob !== "undefined" && body instanceof Blob;
};

const hasMultipartValue = (value: unknown): boolean => {
  if (isBlobBody(value) || isFormDataBody(value)) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(hasMultipartValue);
  }

  if (value && typeof value === "object") {
    return Object.values(value).some(hasMultipartValue);
  }

  return false;
};

const appendFormDataValue = (
  formData: FormData,
  key: string,
  value: unknown,
) => {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendFormDataValue(formData, key, item));
    return;
  }

  if (isBlobBody(value)) {
    formData.append(key, value);
    return;
  }

  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value));
    return;
  }

  formData.append(key, String(value));
};

const toFormData = (body: Record<string, unknown>) => {
  const formData = new FormData();

  Object.entries(body).forEach(([key, value]) => {
    appendFormDataValue(formData, key, value);
  });

  return formData;
};

const resolveBody = <TBody>(body: TBody) => {
  if (isFormDataBody(body)) {
    return body;
  }

  if (body && typeof body === "object" && hasMultipartValue(body)) {
    return toFormData(body as Record<string, unknown>);
  }

  return body;
};

export async function apiv2Fetch<
  TData,
  TError,
  TBody extends {} | FormData | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
  getToken,
  baseUrl = "",
}: Apiv2FetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams
>): Promise<TData> {
  let error: ErrorWrapper<TError>;

  try {
    const resolvedBody = resolveBody(body);
    const isFormData = isFormDataBody(resolvedBody);

    const requestHeaders: HeadersInit = {
      ...headers,
    };

    if (!isFormData) {
      requestHeaders["Content-Type"] = "application/json";
    }

    const token = await getToken();

    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    const response = await window.fetch(
      `${baseUrl}${resolveUrl(url, method, queryParams, pathParams)}`,
      {
        signal,
        method: method.toUpperCase(),
        body: resolvedBody
          ? isFormData
            ? resolvedBody
            : JSON.stringify(resolvedBody)
          : undefined,
        headers: requestHeaders,
        credentials: "include",
      },
    );

    if (!response.ok) {
      try {
        error = {
          status: response.status,
          payload: await response.json(),
        } as ErrorWrapper<TError>;
      } catch (e) {
        error = {
          status: "unknown" as const,
          payload: {
            message:
              e instanceof Error
                ? `Unexpected error (${e.message})`
                : "Unexpected error",
            timestamp: new Date().toISOString(),
          },
        };
      }
    } else if (response.headers.get("content-type")?.includes("json")) {
      return await response.json();
    } else {
      return (await response.blob()) as unknown as TData;
    }
  } catch (e) {
    const errorObject: Error = {
      name: "unknown" as const,
      message:
        e instanceof Error ? `Network error (${e.message})` : "Network error",
      stack: e as string,
    };

    throw errorObject;
  }

  throw error;
}

const resolveUrl = (
  url: string,
  method: string,
  queryParams: Record<string, unknown> = {},
  pathParams: Record<string, string> = {},
) => {
  const path = url.replace(
    /\{\w*\}/g,
    (key) => pathParams[key.slice(1, -1)] ?? "",
  );
  const serialization =
    queryParameterSerialization[`${method.toLowerCase()} ${url}`] ?? {};
  const query = serializeQueryParameters(queryParams, serialization);

  return query ? `${path}?${query}` : path;
};
